import { getVehicleForBrand } from 'tw-oi-core/utils/vehicle'

import { getAllOfflineVehicleRecords } from '../services/offlineStorage'

/**
 * Returns vehicle records that have offline content saved
 *
 * @returns {Promise<Array>} offline vehicle records
 */
export async function getOfflineVehicleRecords() {
  return getAllOfflineVehicleRecords()
}

/**
 * Resolves the vehicle list to use when the app is offline.
 * Filters cached API vehicles to those with downloads, or falls back to
 * vehicle metadata stored at download time.
 *
 * @param {Array|null} cachedVehicles vehicles from Redux
 * @param {string} currentBrand active brand name
 * @returns {Promise<{vehicles: Array, empty: boolean}>}
 */
export async function resolveOfflineVehiclesList(cachedVehicles, currentBrand) {
  const records = await getAllOfflineVehicleRecords()

  if (!records.length) {
    return { vehicles: [], empty: true }
  }

  const offlineIds = new Set(records.map(record => record.vehicleId))

  if (cachedVehicles && cachedVehicles.length) {
    const brandVehicles = getVehicleForBrand(cachedVehicles, currentBrand)
    let vehicles = brandVehicles.filter(vehicle => offlineIds.has(vehicle.id))

    if (currentBrand === 'Forest River') {
      const palominoVehicles = getVehicleForBrand(cachedVehicles, 'Palomino')
      vehicles = [
        ...vehicles,
        ...palominoVehicles.filter(vehicle => offlineIds.has(vehicle.id)),
      ]
    }

    return { vehicles, empty: vehicles.length === 0 }
  }

  const vehicles = records
    .filter(record => record.vehicleInfo)
    .map(record => record.vehicleInfo)
    .filter(vehicle => {
      if (currentBrand === 'Forest River') {
        return vehicle.make === 'Forest River' || vehicle.make === 'Palomino'
      }
      return vehicle.make === currentBrand
    })

  return { vehicles, empty: vehicles.length === 0 }
}

/**
 * Returns every vehicle that has offline content saved, across all brands.
 *
 * @param {Array|null} cachedVehicles vehicles from Redux
 * @returns {Promise<{vehicles: Array, empty: boolean}>}
 */
export async function resolveAllOfflineVehicles(cachedVehicles) {
  const records = await getAllOfflineVehicleRecords()

  if (!records.length) {
    return { vehicles: [], empty: true }
  }

  const offlineIds = new Set(records.map(record => record.vehicleId))
  let vehicles = []

  if (cachedVehicles && cachedVehicles.length) {
    vehicles = cachedVehicles.filter(vehicle => offlineIds.has(vehicle.id))
  }

  const knownIds = new Set(vehicles.map(vehicle => vehicle.id))
  records.forEach(record => {
    if (record.vehicleInfo && offlineIds.has(record.vehicleId) && !knownIds.has(record.vehicleId)) {
      vehicles.push(record.vehicleInfo)
      knownIds.add(record.vehicleId)
    }
  })

  vehicles.sort((a, b) => {
    if (a.year !== b.year) {
      return String(b.year).localeCompare(String(a.year))
    }
    if (a.make !== b.make) {
      return a.make.localeCompare(b.make)
    }
    return a.model.localeCompare(b.model)
  })

  return { vehicles, empty: vehicles.length === 0 }
}

/**
 * Finds an offline vehicle record matching a guide route year/model slug
 *
 * @param {Array} records offline vehicle records
 * @param {string} currentBrand active brand name
 * @param {string} year model year
 * @param {string} model path alias slug
 * @param {Function} getPathAlias path alias helper
 * @returns {object|null} matching record or null
 */
export function findOfflineVehicleRecord(records, currentBrand, year, model, getPathAlias) {
  return records.find(record => {
    const vehicle = record.vehicleInfo
    if (!vehicle) return false

    const brandMatch =
      currentBrand === 'Forest River'
        ? vehicle.make === 'Forest River' || vehicle.make === 'Palomino'
        : vehicle.make === currentBrand

    return (
      brandMatch &&
      String(vehicle.year) === String(year) &&
      getPathAlias(`${vehicle.id} ${vehicle.model}`) === model
    )
  }) || null
}
